// User management API functions
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';

// Helper function to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    // Special handling for quota/rate limit errors (HTTP 429)
    if (response.status === 429) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (parseError) {
        // If JSON parsing fails, show a generic quota message
        throw new Error(`Rate Limit Exceeded\n\nYou've made too many requests. Please wait a minute and try again.\n\nTip: Upgrade your subscription for higher limits!`);
      }
      
      // Successfully parsed error data - build detailed message
      const limitType = errorData?.limit_type === 'monthly' ? 'Monthly Quota' : 'Rate Limit';
      const retryAfter = response.headers.get('Retry-After');
      const tier = errorData?.subscription_tier || 'current plan';
      
      // Build user-friendly message
      let quotaMessage = `${limitType} Exceeded\n\n`;
      quotaMessage += errorData?.detail || `You've exceeded the ${limitType.toLowerCase()} for the ${tier}.`;
      
      // Add usage stats
      if (errorData?.current_usage && errorData?.limit) {
        quotaMessage += `\n\nUsage: ${errorData.current_usage}/${errorData.limit} requests`;
      }
      
      // Add retry information
      if (retryAfter) {
        const retrySeconds = parseInt(retryAfter);
        if (retrySeconds < 60) {
          quotaMessage += `\n\nPlease wait ${retrySeconds} seconds before trying again.`;
        } else {
          const minutes = Math.ceil(retrySeconds / 60);
          quotaMessage += `\n\nPlease wait ${minutes} minute${minutes > 1 ? 's' : ''} before trying again.`;
        }
      }
      
      // Add upgrade suggestion + plan details
      const recommendedPlan = errorData?.reccomended_plan || errorData?.recommended_plan || errorData?.next_tier;
      quotaMessage += `\n\nTip: Upgrade your subscription for higher limits!`;
      if (recommendedPlan) {
        quotaMessage += `\nRecommended plan: ${recommendedPlan}`;
      }
      
      const err = new Error(quotaMessage);
      // Attach rich fields so UI can render clickable link
      err.limitType = limitType;
      err.subscriptionTier = tier;
      err.recommendedPlan = recommendedPlan || null;
      throw err;
    }
    
    // Handle other errors
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
  }
  return response.json();
};

// Helper function to make authenticated API requests
const authenticatedRequest = async (endpoint, options = {}, token = null) => {
  // If no token provided, try to get it from localStorage
  if (!token) {
    token = authUtils.getToken();
  }
  
  const url = `${API_BASE_URL}${endpoint}`;
  const config = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
      ...(token && { 'Authorization': `Bearer ${token}` }),
    },
  };

  console.log('Making API request to:', url);
  console.log('Request config:', config);
  console.log('Token being sent:', token);
  
  const response = await fetch(url, config);
  console.log('Response status:', response.status);
  console.log('Response headers:', Object.fromEntries(response.headers.entries()));
  
  if (!response.ok) {
    const errorText = await response.text();
    console.log('Error response body:', errorText);
  }
  
  return handleResponse(response);
};

// User Authentication API
export const authAPI = {
  // Register a new user
  register: async (userData) => {
    const response = await fetch(`${API_BASE_URL}/users/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });
    return handleResponse(response);
  },

  // Login user
  login: async (credentials) => {
    const response = await fetch(`${API_BASE_URL}/users/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });
    return handleResponse(response);
  },

  // Get current user profile
  getProfile: async (token) => {
    return authenticatedRequest('/users/profile', {
      method: 'GET',
    }, token);
  },

  // Update user profile
  updateProfile: async (profileData, token) => {
    return authenticatedRequest('/users/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    }, token);
  },

  // Verify email
  verifyEmail: async (token) => {
    return authenticatedRequest('/users/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  },

  // Forgot password
  forgotPassword: async (email) => {
    return authenticatedRequest('/users/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  // Reset password
  resetPassword: async (resetData) => {
    return authenticatedRequest('/users/reset-password', {
      method: 'POST',
      body: JSON.stringify(resetData),
    });
  },
};

// API Key Management API
export const apiKeyAPI = {
  // Create new API key
  create: async (keyData, token) => {
    return authenticatedRequest('/users/api-keys', {
      method: 'POST',
      body: JSON.stringify(keyData),
    }, token);
  },

  // Get user's API keys
  list: async (token) => {
    return authenticatedRequest('/users/api-keys', {
      method: 'GET',
    }, token);
  },

  // Update API key
  update: async (keyId, updateData, token) => {
    return authenticatedRequest(`/users/api-keys/${keyId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData),
    }, token);
  },

  // Revoke API key
  revoke: async (keyId, token) => {
    return authenticatedRequest(`/users/api-keys/${keyId}`, {
      method: 'DELETE',
    }, token);
  },
};

// Admin Management API
export const adminAPI = {
  // Get current user (for role checking)
  getCurrentUser: async () => {
    const token = authUtils.getToken();
    return authenticatedRequest('/users/profile', {
      method: 'GET',
    }, token);
  },

  // Get pending users
  getPendingUsers: async () => {
    const token = authUtils.getToken();
    return authenticatedRequest('/users/admin/pending', {
      method: 'GET',
    }, token);
  },

  // Activate user
  activateUser: async (userId) => {
    const token = authUtils.getToken();
    return authenticatedRequest(`/users/admin/activate/${userId}`, {
      method: 'POST',
    }, token);
  },
};

// Subscription Management API
export const subscriptionAPI = {
  // Get all subscription plans
  getPlans: async () => {
    return authenticatedRequest('/subscriptions/plans', {
      method: 'GET',
    });
  },

  // Get specific plan details
  getPlanDetails: async (tier) => {
    return authenticatedRequest(`/subscriptions/plans/${tier}`, {
      method: 'GET',
    });
  },

  // Get current subscription
  getCurrent: async (token) => {
    return authenticatedRequest('/subscriptions/current', {
      method: 'GET',
    }, token);
  },

  // Upgrade subscription
  upgrade: async (tier, billingCycle = 'monthly', token) => {
    return authenticatedRequest(`/subscriptions/upgrade?tier=${tier}&billing_cycle=${billingCycle}`, {
      method: 'POST',
    }, token);
  },

  // Downgrade subscription
  downgrade: async (tier, token) => {
    return authenticatedRequest(`/subscriptions/downgrade?tier=${tier}`, {
      method: 'POST',
    }, token);
  },

  // Cancel subscription
  cancel: async (token) => {
    return authenticatedRequest('/subscriptions/cancel', {
      method: 'POST',
    }, token);
  },

  // Get usage statistics
  getUsage: async (token) => {
    return authenticatedRequest('/subscriptions/usage', {
      method: 'GET',
    }, token);
  },

  // Get billing information
  getBilling: async (token) => {
    return authenticatedRequest('/subscriptions/billing', {
      method: 'GET',
    }, token);
  },

  // Get subscription limits
  getLimits: async (token) => {
    return authenticatedRequest('/subscriptions/limits', {
      method: 'GET',
    }, token);
  },

  // Get available features
  getFeatures: async (token) => {
    return authenticatedRequest('/subscriptions/features', {
      method: 'GET',
    }, token);
  },
};

// Stripe Billing API
export const billingAPI = {
  // Create Stripe checkout session
  createCheckout: async (tier, billingCycle = 'monthly', token) => {
    return authenticatedRequest('/billing/checkout', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        tier: tier,
        billing_cycle: billingCycle
      })
    }, token);
  },

  // Create customer portal session
  createPortal: async (token) => {
    return authenticatedRequest('/billing/customer-portal', {
      method: 'GET',
    }, token);
  },

  // Sync checkout session (call after successful checkout)
  syncSession: async (sessionId, token) => {
    return authenticatedRequest(`/billing/sync-session?session_id=${sessionId}`, {
      method: 'POST',
    }, token);
  },

  // Get subscription status from Stripe
  getSubscriptionStatus: async (token) => {
    return authenticatedRequest('/billing/subscription-status', {
      method: 'GET',
    }, token);
  },

  // Cancel subscription
  cancelSubscription: async (subscriptionId, token) => {
    return authenticatedRequest('/billing/cancel-subscription', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        subscription_id: subscriptionId
      })
    }, token);
  },
};

// Utility functions
export const authUtils = {
  // Store auth token in localStorage
  setToken: (token) => {
    console.log('Storing token in localStorage:', token);
    localStorage.setItem('auth_token', token);
  },

  // Get auth token from localStorage
  getToken: () => {
    const token = localStorage.getItem('auth_token');
    console.log('Retrieved token from localStorage:', token);
    return token;
  },

  // Remove auth token from localStorage
  removeToken: () => {
    localStorage.removeItem('auth_token');
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    return !!localStorage.getItem('auth_token');
  },

  // Store user data in localStorage
  setUser: (userData) => {
    localStorage.setItem('user_data', JSON.stringify(userData));
  },

  // Get user data from localStorage
  getUser: () => {
    const userData = localStorage.getItem('user_data');
    return userData ? JSON.parse(userData) : null;
  },

  // Remove user data from localStorage
  removeUser: () => {
    localStorage.removeItem('user_data');
  },

  // Logout user
  logout: () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
  },
};

// Admin Analytics API
export const adminAnalyticsAPI = {
  // Get subscription analytics (admin only)
  getSubscriptionAnalytics: async (token) => {
    return authenticatedRequest('/subscriptions/admin/analytics', {
      method: 'GET',
    }, token);
  },

  // Get API usage analytics (admin only)
  getAPIUsageAnalytics: async (filters = {}, token) => {
    const params = new URLSearchParams();
    if (filters.range && filters.range !== 'all') {
      params.append('range', filters.range);
    } else if (filters.days && filters.days !== 'all') {
      params.append('days', filters.days);
    }
    if (filters.plan && filters.plan !== 'all') {
      params.append('plan', filters.plan);
    }
    if (filters.category && filters.category !== 'all') {
      params.append('category', filters.category);
    }
    if (filters.model_type && filters.model_type !== 'all') {
      params.append('model_type', filters.model_type);
    }
    
    const queryString = params.toString();
    const url = `/admin/api-usage${queryString ? `?${queryString}` : ''}`;
    
    return authenticatedRequest(url, {
      method: 'GET',
    }, token);
  },

  // Get webhook events (admin only)
  getWebhookEvents: async (token) => {
    return authenticatedRequest('/subscriptions/admin/webhook-events', {
      method: 'GET',
    }, token);
  },

  // Export admin data (CSV or JSON)
  exportAdminData: async (format, token) => {
    const response = await fetch(`${API_BASE_URL}/subscriptions/admin/export?format=${format}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Export failed: ${response.statusText}`);
    }

    // Handle file download
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `admin-analytics.${format}`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  },
};

// Convenience functions for components
export const getSubscriptionAnalytics = async () => {
  const token = authUtils.getToken();
  return adminAnalyticsAPI.getSubscriptionAnalytics(token);
};

export const getWebhookEvents = async () => {
  const token = authUtils.getToken();
  return adminAnalyticsAPI.getWebhookEvents(token);
};

export const exportAdminData = async (format) => {
  const token = authUtils.getToken();
  return adminAnalyticsAPI.exportAdminData(format, token);
};

// Default export
export default {
  authAPI,
  apiKeyAPI,
  subscriptionAPI,
  adminAnalyticsAPI,
  authUtils,
};
